import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import Tooltip from "@mui/material/Tooltip";
import classNames from "classnames";
import "./SummaryFooter.scss";
import SkeletonLoader from "../SkeletonLoader/SkeletonLoader";
import { Loader } from "../Loader/Loader";
import { useSelectedResult } from "../../contexts/SelectedResultContext";
import { TermsAndConditions } from "../../Pages/AgentTerms/AgentTerms";
import { useOnClickOutside } from "../../utils/ComponentsUtils";
import { getPackageProfilePriceDisplay } from "../../utils/packageProfilePriceDisplay";
import { PACKAGE_PROFILE_PAYMENT_BANK_TRANSFER } from "../../utils/packageProfileCustomerPayment";
import {
  isSummaryFooterButtonDisabled,
  logSummaryFooterDisabledTrace,
  resolveSummaryFooterShowsActions,
  summaryFooterDisabledReason,
} from "../../utils/summaryFooterInteraction";
import { logBankTransferButtonClickTrace } from "../../utils/packageProfileBankTransferFlow";

const SummaryFooter = ({
  moveNextStage,
  movePreviousStage,
  beforePayment,
  nextStageDisabled,
  loading,
  isConditionsConfirmed,
  setIsConditionsConfirmed,
  isCustomerLink = false,
  isCustomerPaymentStage = false,
  customerPaymentMethod,
  currentStageIndex = 0,
  totalStages = 3,
  showTermsCheckbox = true,
  billableAttractionsIls = 0,
  persistedPublicOfferIls,
  persistedPublicOfferUnavailable = false,
  footerPriceTextOverride,
  continueButtonLabel,
  /** הסתרת כפתור ההמשך בפוטר (טיסות סוכן — שני ה-CTA בכרטיס התשלום). */
  showContinueButton = true,
  /** כמו חבילות לפני תשלום: הצגת צ׳קבוקס תנאים גם כש־beforePayment=false (טיסות סוכן — סיכום). */
  termsCheckboxBeforeContinue = false,
  beforePaymentPrimaryLabel,
  beforePaymentSecondaryLabel,
  /** כפתור תשלום יחיד לפי customerPaymentMethod (אשראי / העברה) בלי זרימת לקוח מלאה */
  usePaymentMethodPrimaryButton = false,
  /** טקסט לכפתור הראשי; אם ריק — ברירת מחדל לפי אמצעי התשלום */
  paymentMethodPrimaryLabel,
  /** הצעת לקוח (?offer=1) — חזרה לעמוד סיכום ההצעה */
  showReturnToOfferButton = false,
  onReturnToOffer = null,
  showCustomerContinueButton = true,
  showCustomerBackButton = true,
  showFooterPaymentPrimary = true,
  /** offer=1 — כפתורי ניווט בסיידבר בלבד; פוטר = מחיר */
  showFooterCustomerNav = true,
}) => {
  const {
    ourPrice,
    ilsToUsd,
    packageProfilePriceBlockMessage,
    packageProfileDisplayOurPrice,
  } = useSelectedResult();
  const footerPrice = React.useMemo(() => {
    const trimmed =
      footerPriceTextOverride != null
        ? String(footerPriceTextOverride).trim()
        : "";
    if (trimmed) {
      return {
        loading: false,
        cardPrimary: null,
        cardSecondary: null,
        footerPriceText: trimmed,
      };
    }
    const matrixBlocked =
      packageProfilePriceBlockMessage != null &&
      String(packageProfilePriceBlockMessage).trim() !== "";
    if (persistedPublicOfferUnavailable) {
      return getPackageProfilePriceDisplay(packageProfileDisplayOurPrice, ilsToUsd, {
        billableAttractionsIls,
        priceUnavailable: true,
      });
    }
    if (matrixBlocked) {
      return getPackageProfilePriceDisplay(packageProfileDisplayOurPrice, ilsToUsd, {
        billableAttractionsIls,
        priceUnavailable: true,
        priceUnavailableLabel: String(packageProfilePriceBlockMessage).trim(),
      });
    }
    if (
      persistedPublicOfferIls != null &&
      Number.isFinite(Number(persistedPublicOfferIls))
    ) {
      return getPackageProfilePriceDisplay(
        {
          ...(packageProfileDisplayOurPrice ?? ourPrice),
          priceWithCreditCardFee: Number(persistedPublicOfferIls),
        },
        ilsToUsd,
        { billableAttractionsIls }
      );
    }
    return getPackageProfilePriceDisplay(packageProfileDisplayOurPrice, ilsToUsd, {
      billableAttractionsIls,
    });
  }, [
    footerPriceTextOverride,
    ourPrice,
    packageProfileDisplayOurPrice,
    ilsToUsd,
    billableAttractionsIls,
    persistedPublicOfferIls,
    persistedPublicOfferUnavailable,
    packageProfilePriceBlockMessage,
  ]);

  const footerHasPrice =
    (footerPriceTextOverride != null &&
      String(footerPriceTextOverride).trim() !== "") ||
    persistedPublicOfferUnavailable ||
    (persistedPublicOfferIls != null &&
      Number.isFinite(Number(persistedPublicOfferIls))) ||
    packageProfileDisplayOurPrice?.priceWithCreditCardFee != null ||
    ourPrice?.priceWithCreditCardFee != null ||
    (packageProfilePriceBlockMessage != null &&
      String(packageProfilePriceBlockMessage).trim() !== "");

  const { t } = useTranslation();
  const [isTermsModal, setIsTermsModal] = useState(false);
  const modalRef = React.createRef();
  useOnClickOutside(modalRef, () => setIsTermsModal(false));

  const showFooterActions = resolveSummaryFooterShowsActions({
    loading,
    isCustomerLink,
    showFooterCustomerNav,
  });
  const buttonDisabled = isSummaryFooterButtonDisabled(nextStageDisabled);
  const disabledReason = summaryFooterDisabledReason(nextStageDisabled);

  React.useEffect(() => {
    if (!buttonDisabled) return;
    logSummaryFooterDisabledTrace({
      reason: disabledReason,
      isCustomerLink,
      beforePayment,
      isCustomerPaymentStage,
      showFooterCustomerNav,
      showFooterActions,
    });
  }, [
    buttonDisabled,
    disabledReason,
    isCustomerLink,
    beforePayment,
    isCustomerPaymentStage,
    showFooterCustomerNav,
    showFooterActions,
  ]);

  const invokeMoveNext = (paymentTiming) => {
    if (paymentTiming === "later") {
      logBankTransferButtonClickTrace({
        source: "SummaryFooter.invokeMoveNext",
        buttonDisabled,
        disabledReason,
        hasMoveNextHandler: typeof moveNextStage === "function",
        beforePayment,
        isCustomerLink,
        isCustomerPaymentStage,
        showFooterActions,
        currentStageIndex,
      });
    }
    if (buttonDisabled || typeof moveNextStage !== "function") {
      if (paymentTiming === "later") {
        logBankTransferButtonClickTrace({
          source: "SummaryFooter.invokeMoveNext",
          blocked: true,
          reason: buttonDisabled ? disabledReason || "button_disabled" : "missing_moveNextStage",
        });
      }
      return;
    }
    if (paymentTiming === undefined) {
      moveNextStage();
      return;
    }
    moveNextStage(paymentTiming);
  };

  const renderActionButtons = () => {
    if (!showContinueButton) {
      return null;
    }
    if (loading) {
      return (
        <div
          className="summary-footer__actions summary-footer__actions--loading"
          data-testid="summary-footer-actions"
        >
          <Loader />
        </div>
      );
    }
    if (!showFooterActions) {
      return null;
    }

    if (
      usePaymentMethodPrimaryButton &&
      typeof customerPaymentMethod === "string" &&
      customerPaymentMethod
    ) {
      return (
        <div
          className="summary-footer__actions customer-navigation-buttons"
          data-testid="summary-footer-actions"
        >
          <button
            key="payment-method-primary"
            type="button"
            data-testid="summary-footer-pay-primary"
            disabled={buttonDisabled}
            onClick={() =>
              invokeMoveNext(
                customerPaymentMethod === PACKAGE_PROFILE_PAYMENT_BANK_TRANSFER
                  ? "later"
                  : "now"
              )
            }
            className={classNames(
              "order-package-btn order-package-btn-pay-now tw-text-sm lg:tw-text-base",
              { disabled: buttonDisabled }
            )}
          >
            {paymentMethodPrimaryLabel
              ? paymentMethodPrimaryLabel
              : customerPaymentMethod === PACKAGE_PROFILE_PAYMENT_BANK_TRANSFER
                ? "צור הזמנה ושלח אישור העברה"
                : t("packages.pay-now")}
          </button>
        </div>
      );
    }

    if (isCustomerLink && showFooterCustomerNav) {
      return (
        <div
          className="summary-footer__actions customer-navigation-buttons"
          data-testid="summary-footer-actions"
        >
          {showReturnToOfferButton && typeof onReturnToOffer === "function" ? (
            <button
              type="button"
              onClick={onReturnToOffer}
              className="order-package-btn order-package-btn-back order-package-btn-back-to-offer tw-text-sm lg:tw-text-base"
            >
              חזרה להצעה
            </button>
          ) : null}
          {showCustomerBackButton && currentStageIndex > 0 ? (
            <button
              type="button"
              onClick={() => movePreviousStage && movePreviousStage()}
              className="order-package-btn order-package-btn-back tw-text-sm lg:tw-text-base"
            >
              חזור
            </button>
          ) : null}
          {beforePayment && showCustomerContinueButton ? (
            <Tooltip title={disabledReason}>
              <span>
                <button
                  type="button"
                  data-testid="summary-footer-continue"
                  disabled={buttonDisabled}
                  onClick={() => invokeMoveNext()}
                  className={classNames(
                    "order-package-btn tw-text-sm lg:tw-text-base",
                    { disabled: buttonDisabled }
                  )}
                >
                  התקדם
                </button>
              </span>
            </Tooltip>
          ) : null}
          {!beforePayment && !isCustomerPaymentStage && showCustomerContinueButton ? (
            <Tooltip title={disabledReason}>
              <span>
                <button
                  type="button"
                  data-testid="summary-footer-continue"
                  disabled={buttonDisabled}
                  onClick={() => invokeMoveNext()}
                  className={classNames(
                    "order-package-btn tw-text-sm lg:tw-text-base",
                    { disabled: buttonDisabled }
                  )}
                >
                  התקדם
                </button>
              </span>
            </Tooltip>
          ) : null}
          {isCustomerPaymentStage && showFooterPaymentPrimary ? (
            <button
              key="customer-payment-primary"
              type="button"
              data-testid="summary-footer-pay-primary"
              disabled={buttonDisabled}
              onClick={() =>
                invokeMoveNext(
                  customerPaymentMethod === PACKAGE_PROFILE_PAYMENT_BANK_TRANSFER
                    ? "later"
                    : "now"
                )
              }
              className={classNames(
                "order-package-btn order-package-btn-pay-now tw-text-sm lg:tw-text-base",
                { disabled: buttonDisabled }
              )}
            >
              {customerPaymentMethod === PACKAGE_PROFILE_PAYMENT_BANK_TRANSFER
                ? "צור הזמנה ושלח אישור העברה"
                : t("packages.pay-now")}
            </button>
          ) : null}
        </div>
      );
    }

    if (beforePayment) {
      return (
        <div
          className="summary-footer__actions buttons summary-footer-agent-before-payment-buttons"
          data-testid="summary-footer-actions"
        >
          <button
            key="pay-now"
            type="button"
            data-testid="summary-footer-pay-now"
            disabled={buttonDisabled}
            onClick={() => invokeMoveNext("now")}
            className={classNames(
              "order-package-btn order-package-btn-pay-now tw-text-sm lg:tw-text-base",
              { disabled: buttonDisabled }
            )}
          >
            {beforePaymentPrimaryLabel || t("packages.pay-now")}
          </button>
          <button
            key="pay-later"
            type="button"
            data-testid="summary-footer-pay-later"
            disabled={buttonDisabled}
            onClick={() => invokeMoveNext("later")}
            className={classNames(
              "order-package-btn order-package-btn-pay-later tw-text-sm lg:tw-text-base",
              { disabled: buttonDisabled }
            )}
          >
            {beforePaymentSecondaryLabel || t("packages.pay-later")}
          </button>
        </div>
      );
    }

    return (
      <div
        className="summary-footer__actions"
        data-testid="summary-footer-actions"
      >
        <Tooltip title={disabledReason}>
          <span>
            <button
              type="button"
              data-testid="summary-footer-continue"
              disabled={buttonDisabled}
              onClick={() => invokeMoveNext()}
              className={classNames(
                "order-package-btn tw-text-sm lg:tw-text-base",
                { disabled: buttonDisabled }
              )}
            >
              {continueButtonLabel || t("packages.continue")}
            </button>
          </span>
        </Tooltip>
      </div>
    );
  };

  const footerNode = (
    <div className="summary-footer" data-testid="summary-footer" dir="rtl">
      <div className="summary-footer-spacer" aria-hidden="true" />
      <div className="summary-footer-content">
        <div className="price-to-pay">
          <div className="tw-hidden lg:tw-block">{t("packages.totalToPay")}:</div>
          {footerHasPrice ? (
            <span translate="no">{footerPrice.footerPriceText}</span>
          ) : (
            <SkeletonLoader style={{ width: 70, height: 20, marginRight: 10 }} />
          )}
        </div>

        {((beforePayment && showTermsCheckbox) ||
          (termsCheckboxBeforeContinue && showTermsCheckbox)) && (
          <div className="summary-footer-checkbox">
            <input
              type="checkbox"
              className="summary-footer-checkbox-input"
              required
              onChange={(e) => setIsConditionsConfirmed(e.target.checked)}
              checked={isConditionsConfirmed}
            />
            <span className="summary-footer-checkbox-text-black">
              {`${t("completepassengers.approve")}`}
            </span>
            <span
              className="summary-footer-checkbox-text"
              onClick={() => setIsTermsModal(true)}
            >
              תנאי ביטול ותקנון
            </span>
          </div>
        )}
      </div>

      {renderActionButtons()}

      {isTermsModal ? (
        <div className="passangerData-popup-wrapper">
          <div ref={modalRef} className="passangerData-popup-frame tw-pt-4">
            <TermsAndConditions />
            <div className="passangerData-popup-agree-btn-wrapper">
              <button
                type="button"
                className="passangerData-popup-agree-btn"
                onClick={() => setIsTermsModal(false)}
              >
                {t("completepassengers.close")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );

  if (typeof document !== "undefined") {
    return createPortal(footerNode, document.body);
  }

  return footerNode;
};

export default SummaryFooter;
